import apiClient from './api';
import axios from 'axios';
import { Artifact, Finding } from '../types/api.types';

/**
 * Service for managing artifacts and findings
 */
export class ArtifactService {
  private artifactsUrl = '/pentest/ui/artifacts';
  private findingsUrl = '/pentest/ui/findings';

  // Artifacts
  async getArtifacts(projectId: string): Promise<Artifact[]> {
    return apiClient.get<Artifact[]>(`${this.artifactsUrl}?project_id=${projectId}`);
  }

  async createArtifact(artifact: { name: string; type: string; project_id: string; content?: any; is_manual?: boolean }): Promise<Artifact> {
    return apiClient.post<Artifact>(this.artifactsUrl, artifact);
  }

  async deleteArtifact(id: string): Promise<void> {
    return apiClient.delete<void>(`${this.artifactsUrl}/${id}`);
  }

  async uploadArtifactFile(artifactId: string, file: File, projectId: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_id', projectId);
    formData.append('artifact_source', 'manual_upload');
    
    return apiClient.postForm<any>(`${this.artifactsUrl}/${artifactId}/file`, formData, {
      timeout: 120000 // 2 minutes timeout for file uploads
    });
  }

  getArtifactFileUrl(artifactId: string): string {
    return `${apiClient.getBaseUrl()}${this.artifactsUrl}/${artifactId}/file`;
  }

  async getArtifactFileContent(artifactId: string): Promise<string> {
    try {
      const response = await apiClient.get<string>(`${this.artifactsUrl}/${artifactId}/file`, {
        responseType: 'text',
        timeout: 60000 // Increase timeout to 60 seconds for file content
      });
      return response;
    } catch (error) {
      console.error('Error fetching artifact file content:', error);
      throw error;
    }
  }

  async getArtifactProcessingResults(artifactId: string): Promise<any> {
    return apiClient.get<any>(`${this.artifactsUrl}/${artifactId}/processing`);
  }

  // Findings
  async getFindings(projectId: string): Promise<Finding[]> {
    return apiClient.get<Finding[]>(`${this.findingsUrl}?project_id=${projectId}`);
  }

  async createFinding(finding: { 
    name: string; 
    description?: string; 
    severity: 'low' | 'medium' | 'high' | 'critical';
    project_id: string;
  }): Promise<Finding> {
    return apiClient.post<Finding>(this.findingsUrl, finding);
  }

  async deleteFinding(id: string): Promise<void> {
    return apiClient.delete<void>(`${this.findingsUrl}/${id}`);
  }

  async uploadFindingFile(findingId: string, file: File, projectId: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_id', projectId);
    
    return apiClient.postForm<any>(`${this.findingsUrl}/${findingId}/file`, formData);
  }

  getFindingFileUrl(findingId: string): string {
    return `${apiClient.getBaseUrl()}${this.findingsUrl}/${findingId}/file`;
  }

  /**
   * Check if a file is an image based on its MIME type
   */
  isImageFile(mimeType: string): boolean {
    return mimeType.startsWith('image/');
  }

  /**
   * Check if a file is a PDF based on its MIME type
   */
  isPdfFile(mimeType: string): boolean {
    return mimeType === 'application/pdf';
  }

  /**
   * Check if a file is a text file based on its MIME type
   */
  isTextFile(mimeType: string): boolean {
    return mimeType.startsWith('text/') || 
           mimeType === 'application/json' ||
           mimeType === 'application/xml' ||
           mimeType === 'application/javascript' ||
           this.isMarkdownFile(mimeType);
  }
  
  /**
   * Check if a file type is markdown
   */
  isMarkdownFile(fileType: string): boolean {
    return /^text\/markdown/i.test(fileType) || 
           ['.md', '.markdown', 'markdown', 'md'].includes(fileType.toLowerCase());
  }
}

// Export singleton instance
export const artifactService = new ArtifactService();
export default artifactService;
